<script>
import DiscountCodeComponent from './DiscountCodeComponent.vue';
import { computed, inject, onMounted } from 'vue';
import { checkPoints } from '../strings/ServiceTableCheckPoints';
import { useAppDataStore } from '../stores/appDataStore';

export default {
  components: {
    DiscountCodeComponent
  },
  props: {
    appDataS3Value: {
      type: String,
      required: true
    },
    tooltipContent: {
      type: Object,
      required: true
    },
    tooltipContentWl: {
      type: Object,
      required: true
    },
    preCalculatedAppPackageData: {
      type: Object,
      required: true
    },
    appThemeBrand: {
      type: String,
      default: 'default'
    },
  },
  setup(props, { emit }) {
    const globalDiscountAvailable = inject('discountAvailable');
    const whiteLabelsWithBlueColumn = [ // White labels that have the komfort package column background in blue color
      'default',
      'seniorenportal',
      'mitpflegeleben',
      'hausnotruf-testen',
      'burda',
      'pflegedienst-angela',
      'stadtwerke-heide',
      'stadtwerke-greven',
      'zbvv',
      'spiribo',
      'sanimed',
      'seniorenfocus',
      'corporatebenefits'
    ];

    onMounted(() => {})

    const appDataStore = useAppDataStore();
    const appData = computed(() => appDataStore.appData);

    // Replaces the dot . with a comma , in prices
    const replaceDot = (value) => {
      return value.replace('.', ',');
    }

    // Checks if only the Basis package is available in step 5. Do not apply discount to this package
    const onlyBasisPackageAvailable = () => {
      return props.preCalculatedAppPackageData.basis && (!props.preCalculatedAppPackageData.komfort && !props.preCalculatedAppPackageData.premium);
    }

    const setSelectedPackage = (pack) => {
      let userSelectedPackage = props.preCalculatedAppPackageData[pack]
      let selectedPrice = userSelectedPackage.hasMonatlicherDiscount === true ? userSelectedPackage.discountedPrices.monatlicher : userSelectedPackage.priceFromApi
      appData.value.s5.userSelectedPrice = selectedPrice;
      appData.value.s5.value = pack;
    }

    return {
      replaceDot,
      onlyBasisPackageAvailable,
      setSelectedPackage,
      whiteLabelsWithBlueColumn,
      globalDiscountAvailable,
      checkPoints,
      appData
    }
  }
}
</script>

<template>
  <!-- Discount code component is only available for coporatebenefits and applies only to Komfort & Premium packages -->
  <DiscountCodeComponent v-if="!onlyBasisPackageAvailable() && appThemeBrand === 'corporatebenefits'" />
  <div class="desktop-table__wrapper">
    <div id="desktop-service-table">
      <!-- Package rows -->
      <div class="row packages">
        <div class="package-title-first-hidden first-column"> / </div>
        <div
          v-for="(packIndex, pack) in preCalculatedAppPackageData"
          :key="packIndex"
          class="package-text item"
        >
          <span> {{ pack.replace('_', ' ') }} </span>
          <div v-if="pack === 'komfort'" class="recommend"> Unsere Empfehlung! </div>
        </div>
      </div>

      <!-- Checkpoints rows -->
      <div
        v-for="(point, pointIndex) in checkPoints"
        :key="pointIndex"
        class="row checkpoint">
        <div class="checkpoint-text first-column">
          <span v-html="point.title"></span>
          <div v-tooltip="{html: true, content: appThemeBrand === 'default' ? tooltipContent[point.tooltipContent] : tooltipContentWl[point.tooltipContent]}"
               class="icon icon--help-second" />
        </div>
        <div
          v-for="(packIndex, pack) in preCalculatedAppPackageData"
          :key="packIndex"
          class="item"
          :class="{komfort: whiteLabelsWithBlueColumn.includes((appThemeBrand)) && pack === 'komfort'}"
        >
          <i
            class="icon"
            :class="{
              'icon--check': point.packages.includes(pack),
              'icon--not-checked': !point.packages.includes(pack)
            }" />
        </div>
      </div>

      <!-- Monatlicher Price rows -->
      <div class="row price">
        <div class="price-text first-column">
          <span>Monatlicher Preis *</span>
        </div>
        <div
          v-for="(packIndex, pack) in preCalculatedAppPackageData"
          :key="packIndex"
          class="item price-item"
          :class="{komfort: whiteLabelsWithBlueColumn.includes((appThemeBrand)) && pack === 'komfort'}"
        >
          <div v-if="!packIndex.hasMonatlicherDiscount">
            <span> {{ replaceDot(packIndex.priceFromApi.toFixed(2)) }}€ </span>
          </div>
          <div
            v-else
            class="discounted"
          >
            <span class="price__current"> {{ replaceDot(packIndex.discountedPrices.monatlicher.toFixed(2)) }}€ </span>
            <s>
              {{ replaceDot(packIndex.priceFromApi.toFixed(2)) }}€
            </s>
          </div>
        </div>
      </div>

      <!-- Einmaliges Price rows -->
      <div class="row price">
        <div class="price-text first-column">
          <span>Einmaliges Einrichtungsentgelt </span>
          <span v-if="appDataS3Value === 'Ja' || appDataS3Value === 'Nein'">*</span>
        </div>
        <div
          v-for="(packIndex, pack) in preCalculatedAppPackageData"
          :key="packIndex"
          class="item price-item"
          :class="{komfort: whiteLabelsWithBlueColumn.includes((appThemeBrand)) && pack === 'komfort'}"
        >
          <div v-if="!packIndex.hasEinmaligesDiscount">
            <span> {{ replaceDot(packIndex.einmaligesPrice.toFixed(2)) }}€ </span>
          </div>
          <div
            v-else
            class="discounted"
          >
            <span class="price__current"> {{ replaceDot(packIndex.discountedPrices.einmaliges.toFixed(2)) }}€ </span>
            <s>
              {{ replaceDot(packIndex.einmaligesPrice.toFixed(2)) }}€
            </s>
          </div>
        </div>
      </div>

      <!-- Angebot auswahlen buttons -->
      <div class="row select-package-buttons">
        <div class="first-column"> / </div>
        <button
          v-for="(packIndex, pack) in preCalculatedAppPackageData"
          :key="packIndex"
          class="select-package__button button button--angebot"
          :class="{ active: appData.s5.value === pack }"
          @click="setSelectedPackage(pack)">
          <span>Angebot auswählen</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import '~brandVariables/variables.scss';

.desktop-table__wrapper {
  max-width: 1028px;
}

#desktop-service-table {
  padding: 1rem;
  .row {
    display: flex;
    &.packages {
      gap: 1rem;
      .package-title-first-hidden {
        visibility: hidden;
      }
      .package-text {
        position: relative;
        font-size: 1.6rem;
        font-weight: 700;
        display: flex;
        justify-content: center;
        text-transform: capitalize;
        background: $main-color;
        color: #FAFAFA;
        padding: 1rem 0 !important;
        &.item {
          border: 0;
          border-bottom: .2rem solid #fafafa;
        }
      }
    }
    &.checkpoint {
      gap: 1rem;
      .checkpoint-text {
        position: relative;
        background: $main-color;
      }
      .item {
        .icon {
          font-style: normal;
          &:after {
            font-size: 2.5rem;
          }
        }
      }
    }
    &.price {
      gap: 1rem;
      .price-text {
        background-color: #777777;
      }
      .item {
        background-color: #777;
        color: #FFFFFF;
        padding: 0;
        &.komfort {
          border-top: .2rem solid #fafafa;
          border-bottom: .2rem solid #fafafa;
          background: $main-color;
        }
      }
    }

    &.select-package-buttons {
      align-items: center;
      gap: 1rem;
      .first-column {
        border: none;
      }
      .select-package__button {
        display: flex;
        width: 213px;
      }
    }
  }

  .first-column {
    width: 33%;
    border: .2rem solid #fafafa;
    padding: .8rem 3rem .8rem 1rem;
    color: #FAFAFA;
    font-size: 1.3rem;
    font-weight: 400;
  }

  .item {
    font-size: 1.3rem;
    border: .2rem solid #fafafa;
    padding: .8rem 0;
    width: 213px;
    display: flex;
    justify-content: center;
    align-items: center;
    &.komfort {
      background: $main-color;
      border: 0;
      color: #FFFFFF;
      .icon {
        &:after {
          color: #FFFFFF;
        }
      }
    }
  }
}

  .v-popper--theme-tooltip .v-popper__inner {
    padding: 7px 24px 16px !important;
    font-size: 12px !important;
  }
  @media only screen and (min-width: 1024px) {
    .v-popper--theme-tooltip .v-popper__inner {
      max-width: 400px;
    }
  }

  td.-trans {
    border: none !important;
  }

</style>
